// ===========================================
// Enrollment Card Component
// ===========================================

import { useState } from 'react';
import { getStudentAgeGroup } from '../../../api/users.api';
import {
  Paper,
  Typography,
  Box,
  Button,
  Divider,
  LinearProgress,
  Skeleton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Tooltip,
  Chip,
} from '@mui/material';
import {
  PersonAdd as EnrollIcon,
  PersonRemove as UnenrollIcon,
  Visibility as ViewIcon,
  School as StudentIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { LessonEnrollment } from '../../../api/lessons.api';
import { effectiveStartWeek, formatTermWeekDate } from '../../../utils/dateUtils';
import EditEnrollmentStartWeekDialog from './EditEnrollmentStartWeekDialog';
import { DEFAULT_AGE_GROUP_COLOR } from './lessonDetail.constants';

interface CapacityInfo {
  current: number;
  max: number;
  available: number;
}

/** The class this card belongs to — needed for the start-week chip and its edit dialog. */
export interface EnrollmentCardLesson {
  id: string;
  /** The class name, for the "an invoice may need amending" prompt. */
  name?: string;
  startWeek?: number | null;
  endWeek?: number | null;
  dayOfWeek: number | null;
  Term: { startDate: string; endDate: string; name?: string };
}

interface EnrollmentCardProps {
  /** Optional so the card still renders without a class (no start-week chip / edit). */
  lesson?: EnrollmentCardLesson;
  enrollments: LessonEnrollment[];
  capacity: CapacityInfo | undefined;
  isLoading: boolean;
  isTeacherView: boolean;
  onEnrollClick: () => void;
  onUnenrollClick: (enrollment: LessonEnrollment) => void;
  onViewStudentClick: (student: LessonEnrollment['Student']) => void;
  onToggleWaitlist?: (enrollment: LessonEnrollment) => void;
  isWaitlistLoading?: boolean;
}

export default function EnrollmentCard({
  lesson,
  enrollments,
  capacity,
  isLoading,
  isTeacherView,
  onEnrollClick,
  onUnenrollClick,
  onViewStudentClick,
}: EnrollmentCardProps) {
  // The start week stays editable after the term is LOCKED, so its invoices can be corrected
  // (the one exception to locked terms being read-only, 2026-10-02).
  const [editing, setEditing] = useState<LessonEnrollment | null>(null);

  // ListItemSecondaryAction is absolutely positioned and ListItem only reserves room for ONE
  // button, so with three the start-week chip ran under them on a phone. Reserve the real
  // width: each IconButton is 40px + 8px gap, plus the action's 16px right inset.
  const actionCount = 1 + (!isTeacherView && lesson ? 1 : 0) + (!isTeacherView ? 1 : 0);
  const actionsWidth = actionCount * 48 + 16;

  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h6">
            Enrolled Students
          </Typography>
          {capacity && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
              <LinearProgress
                variant="determinate"
                value={(capacity.current / capacity.max) * 100}
                sx={{ width: 100 }}
                color={capacity.available <= 0 ? 'error' : capacity.current > 0 ? 'success' : 'primary'}
              />
              <Typography variant="body2" color="text.secondary">
                {capacity.current}/{capacity.max} enrolled ({capacity.available} spots available)
              </Typography>
            </Box>
          )}
        </Box>
        {!isTeacherView && (
          <Button
            startIcon={<EnrollIcon />}
            variant="contained"
            onClick={onEnrollClick}
            disabled={capacity?.available === 0}
          >
            Enroll Student
          </Button>
        )}
      </Box>
      <Divider sx={{ mb: 2 }} />

      {isLoading ? (
        <Box>
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} variant="rectangular" height={50} sx={{ mb: 1 }} />
          ))}
        </Box>
      ) : enrollments.length === 0 ? (
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <StudentIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
          <Typography color="text.secondary">
            No students enrolled yet. Click "Enrol Student" to add students.
          </Typography>
        </Box>
      ) : (
        <List>
          {enrollments.map((enrollment: LessonEnrollment) => (
            <ListItem key={enrollment.id} divider sx={{ pr: `${actionsWidth}px` }}>
              <ListItemText
                primary={`${enrollment.Student.firstName} ${enrollment.Student.lastName}`}
                secondary={
                  <Box component="span" sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                    {getStudentAgeGroup(enrollment.Student) ? (
                      <Chip
                        label={getStudentAgeGroup(enrollment.Student)!.name}
                        size="small"
                        sx={{
                          backgroundColor: getStudentAgeGroup(enrollment.Student)!.color || DEFAULT_AGE_GROUP_COLOR,
                          color: 'white',
                        }}
                      />
                    ) : (
                      <Typography variant="caption" color="text.secondary">—</Typography>
                    )}
                    <Typography variant="caption" color="text.secondary">
                      Enrolled: {new Date(enrollment.enrolledAt).toLocaleDateString()}
                    </Typography>
                    {lesson && enrollment.startWeek != null && enrollment.startWeek > effectiveStartWeek(lesson) && (
                      <Chip
                        size="small"
                        variant="outlined"
                        color="info"
                        label={`Starts ${formatTermWeekDate(lesson.Term.startDate, enrollment.startWeek, lesson.dayOfWeek)}`}
                        data-testid="enrolment-start-chip"
                      />
                    )}
                  </Box>
                }
              />
              <ListItemSecondaryAction>
                <Tooltip title="View Student">
                  <IconButton
                    edge="end"
                    onClick={() => onViewStudentClick(enrollment.Student)}
                    sx={{ mr: 1 }}
                  >
                    <ViewIcon />
                  </IconButton>
                </Tooltip>
                {!isTeacherView && lesson && (
                  <Tooltip title="Change starting week">
                    <IconButton
                      edge="end"
                      aria-label={`Change starting week for ${enrollment.Student.firstName} ${enrollment.Student.lastName}`}
                      onClick={() => setEditing(enrollment)}
                      sx={{ mr: 1 }}
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                )}
                {!isTeacherView && (
                  <Tooltip title="Unenrol Student">
                    <IconButton
                      edge="end"
                      color="error"
                      onClick={() => onUnenrollClick(enrollment)}
                    >
                      <UnenrollIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      )}
      {lesson && (
        <EditEnrollmentStartWeekDialog
          open={!!editing}
          onClose={() => setEditing(null)}
          lessonId={lesson.id}
          lesson={lesson}
          term={lesson.Term}
          enrollment={editing}
        />
      )}
    </Paper>
  );
}
